import React, { createContext, useContext, useState, ReactNode } from 'react'
import { notification } from 'antd'
import type { NotificationArgsProps } from 'antd'

type NotificationType = 'success' | 'info' | 'warning' | 'error'

interface NotificationContextType {
  showNotification: (type: NotificationType, title: string, message: string, options?: Partial<NotificationArgsProps>) => void
  showSuccess: (title: string, message: string) => void
  showError: (title: string, message: string) => void
  showWarning: (title: string, message: string) => void
  showInfo: (title: string, message: string) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

interface NotificationProviderProps {
  children: ReactNode
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [api, contextHolder] = notification.useNotification()

  const showNotification = (
    type: NotificationType,
    title: string,
    message: string,
    options: Partial<NotificationArgsProps> = {}
  ) => {
    api[type]({
      message: title,
      description: message,
      placement: 'topRight',
      duration: 4.5,
      ...options
    })
  }

  const showSuccess = (title: string, message: string) => {
    showNotification('success', title, message)
  }

  const showError = (title: string, message: string) => {
    showNotification('error', title, message)
  }

  const showWarning = (title: string, message: string) => {
    showNotification('warning', title, message)
  }

  const showInfo = (title: string, message: string) => {
    showNotification('info', title, message)
  }

  const value: NotificationContextType = {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo
  }

  return (
    <NotificationContext.Provider value={value}>
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider')
  }
  return context
}
